/* ==========================================================================
   Fit Flavors — custom scripts
   --------------------------------------------------------------------------
   Loaded LAST in layout/theme.liquid (after constants.js, pubsub.js,
   global.js and Dawn's feature modules), so everything below can safely use:
     - subscribe() / publish() and PUB_SUB_EVENTS  (from pubsub.js / constants.js)
     - global helpers: trapFocus, removeTrapFocus, debounce, fetchConfig (global.js)

   PUT ALL CUSTOM JS HERE. Do NOT edit global.js or other core modules.
   Prefer HTML/CSS solutions first (Dawn is HTML-first); use JS only for
   behaviour that can't be expressed otherwise (progressive enhancement).

   Wrapped in an IIFE so nothing leaks into global scope. Log each addition
   in CUSTOMIZATIONS.md.
   ========================================================================== */
(function () {
  'use strict';

  // Example — react to cart updates without touching cart-drawer.js:
  // subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
  //   // ...accessibility / behaviour enhancement here
  // });

  /* ------------------------------------------------------------------------
     Zapiet Delivery Validator — accessibility remediation (WCAG 2.4.4 / 4.1.2)
     ------------------------------------------------------------------------
     The Zapiet app injects markup that trips automated a11y scanners
     (axe / WAVE / Lighthouse): empty <h2> headings, an empty submit button,
     icon-only links with no accessible name, and — the "missing form label"
     flags — unlabelled form controls. The delivery-validator has repeatedly
     been rendered TWICE (historically the app-embed plus a legacy S3 script;
     later two duplicate app blocks, deduped 2026-08-05), and whenever it is,
     its IDs are duplicated and the second copy's <label for> no longer
     validly associates. This patch is kept deliberately source-agnostic so it
     keeps working if a duplicate reappears; the Store
     Pickup cart widget (#storePickupApp) also injects selects/inputs with no
     <label>. We can't edit the app's output, so we patch the rendered DOM and
     re-patch on mutation (the app re-renders). Every fix is applied ONLY when
     the element still lacks an accessible name, so we never overwrite
     content/labels the app sets itself.

     Label copy below is a sensible default — adjust to match desired wording.
     ------------------------------------------------------------------------ */
  (function zapietA11y() {
    var LABELS = {
      heading: 'Delivery availability',
      submit: 'Submit',
      close: 'Close',
      primaryAction: 'Confirm',
    };

    function hasAccessibleName(el) {
      return (
        (el.textContent && el.textContent.trim().length > 0) ||
        (el.getAttribute('aria-label') || '').trim().length > 0 ||
        !!el.getAttribute('aria-labelledby') ||
        (el.getAttribute('title') || '').trim().length > 0
      );
    }

    function labelIfEmpty(el, label) {
      if (el && !hasAccessibleName(el)) el.setAttribute('aria-label', label);
    }

    // Accessible-name check for FORM CONTROLS (their name comes from an
    // associated <label>/aria-*, not textContent). el.labels reflects the spec
    // association exactly — for the duplicate-ID orphan it is empty, matching
    // what a scanner sees — so this correctly treats that copy as unlabelled
    // while leaving genuinely-labelled controls alone.
    function fieldHasAccessibleName(el) {
      if ((el.getAttribute('aria-label') || '').trim()) return true;
      if (el.getAttribute('aria-labelledby')) {
        var ids = el.getAttribute('aria-labelledby').split(/\s+/);
        for (var i = 0; i < ids.length; i++) {
          var n = document.getElementById(ids[i]);
          if (n && n.textContent.trim()) return true;
        }
      }
      if (el.labels && el.labels.length) {
        for (var j = 0; j < el.labels.length; j++) {
          if (el.labels[j].textContent.trim()) return true;
        }
      }
      if ((el.getAttribute('title') || '').trim()) return true;
      return false;
    }

    // Keyword → label map, checked against the control's name/id.
    var FIELD_LABELS = [
      [/postal|zip/i, 'Postal code'],
      [/method/i, 'Delivery or pickup method'],
      [/location|store/i, 'Location'],
      [/date/i, 'Date'],
      [/time/i, 'Time'],
      [/instruction|note/i, 'Order instructions'],
      [/quantity|qty/i, 'Quantity'],
      [/search/i, 'Search'],
    ];

    function fieldLabel(el) {
      // 1) Reuse a <label for> that targets this id even if a duplicate id
      //    invalidated the association — matches the first copy's wording
      //    (e.g. the postal-code input → "Enter your postal code").
      if (el.id) {
        var l = document.querySelector('label[for="' + el.id.replace(/"/g, '\\"') + '"]');
        if (l && l.textContent.trim()) return l.textContent.trim();
      }
      // 2) Placeholder.
      var ph = (el.getAttribute('placeholder') || '').trim();
      if (ph) return ph;
      // 3) Keyword map on name/id.
      var key = (el.getAttribute('name') || '') + ' ' + (el.id || '');
      for (var i = 0; i < FIELD_LABELS.length; i++) {
        if (FIELD_LABELS[i][0].test(key)) return FIELD_LABELS[i][1];
      }
      // 4) Humanised name/id fallback.
      var raw = (el.getAttribute('name') || el.id || '')
        .replace(/zapiet[-_]?delivery[-_]?validator__?/i, '')
        .replace(/[_-]+/g, ' ')
        .trim();
      return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Delivery option';
    }

    function patch() {
      // Empty headings: an empty <h2> is flagged by scanners that key off the
      // tag (HTML_CodeSniffer / WAVE) regardless of role, so role="presentation"
      // alone doesn't clear it. Give the empty heading a visually-hidden title
      // so it has real content. Applied only while empty — if the app sets its
      // own heading text, that wins and we leave it untouched.
      document
        .querySelectorAll('h2[id^="zapiet-delivery-validator__modal__container__heading"]')
        .forEach(function (h) {
          if (hasAccessibleName(h)) return;
          if (!h.querySelector('.ff-zapiet-heading-text')) {
            var span = document.createElement('span');
            span.className = 'visually-hidden ff-zapiet-heading-text';
            span.textContent = LABELS.heading;
            h.appendChild(span);
          }
        });

      // Empty submit button(s). Zapiet can render more than one copy of the
      // widget, so label every empty instance — not just the first.
      document
        .querySelectorAll('.zapiet-delivery-validator__submit')
        .forEach(function (el) { labelIfEmpty(el, LABELS.submit); });

      // Icon-only links (close + primary action).
      document
        .querySelectorAll('.zapiet-delivery-validator__close')
        .forEach(function (el) { labelIfEmpty(el, LABELS.close); });
      document
        .querySelectorAll('#zapiet-delivery-validator__modal__primary-action')
        .forEach(function (el) { labelIfEmpty(el, LABELS.primaryAction); });

      // Their icons are decorative once the control has a name: hide the
      // SVGs so scanners stop counting them as images without alt text.
      // Only when the surrounding link or button is named, so an icon is
      // never the control's only label.
      document
        .querySelectorAll('[class*="zapiet-delivery-validator"] svg, svg[class*="zapiet-delivery-validator"]')
        .forEach(function (svg) {
          var control = svg.parentElement && svg.parentElement.closest('a, button');
          if (control && hasAccessibleName(control)) {
            svg.setAttribute('aria-hidden', 'true');
            svg.setAttribute('focusable', 'false');
          }
        });

      // Unlabelled form controls (the "missing form label" flags): the
      // duplicate-ID validator orphans and the Store Pickup cart widget's
      // selects/inputs. aria-label doesn't depend on unique IDs, so set one
      // wherever a control still lacks an accessible name.
      document
        .querySelectorAll(
          '#storePickupApp input, #storePickupApp select, #storePickupApp textarea, ' +
          // The pickup/delivery method container can also render outside the
          // #storePickupApp wrapper (e.g. cart page vs. cart-notification), so
          // target it directly too. Matches both the plural container and each
          // singular .checkoutMethodContainer.
          '[class*="checkoutMethod"] input, ' +
          '[class*="checkoutMethod"] select, ' +
          '[class*="checkoutMethod"] textarea, ' +
          '[id^="zapiet-delivery-validator"] input, ' +
          '[id^="zapiet-delivery-validator"] select, ' +
          '[id^="zapiet-delivery-validator"] textarea'
        )
        .forEach(function (el) {
          var type = (el.getAttribute('type') || '').toLowerCase();
          if (type === 'hidden' || type === 'submit' || type === 'button' || type === 'reset' || type === 'image') return;
          if (fieldHasAccessibleName(el)) return;
          el.setAttribute('aria-label', fieldLabel(el));
        });
    }

    // Is this added node (or any of its descendants) part of the Zapiet widget?
    // Used to ignore unrelated DOM churn (cart drawer, predictive search, etc.).
    function isZapietNode(node) {
      if (node.nodeType !== 1) return false; // elements only
      if (
        node.id &&
        (node.id.indexOf('zapiet-delivery-validator') === 0 ||
          node.id.indexOf('storePickupApp') === 0 ||
          node.id.indexOf('store-pickup') !== -1 ||
          node.id === 'pickup_radio' ||
          node.id === 'delivery_radio')
      )
        return true;
      if (
        typeof node.className === 'string' &&
        (node.className.indexOf('zapiet-delivery-validator') !== -1 ||
          node.className.indexOf('store-pickup') !== -1 ||
          // The method picker's contents can arrive as a later, separate
          // mutation whose node carries none of the markers above — recognise
          // the checkoutMethod(s) container so the re-patch still fires.
          node.className.indexOf('checkoutMethod') !== -1)
      )
        return true;
      return !!(
        node.querySelector &&
        node.querySelector(
          '[id^="zapiet-delivery-validator"], [class*="zapiet-delivery-validator"], #storePickupApp, [class*="store-pickup"], [class*="checkoutMethod"], #pickup_radio, #delivery_radio'
        )
      );
    }

    function init() {
      patch();
      // Debounced re-patch when the app injects/replaces its modal nodes.
      var schedule =
        typeof debounce === 'function'
          ? debounce(patch, 200)
          : (function () {
              var t;
              return function () {
                clearTimeout(t);
                t = setTimeout(patch, 200);
              };
            })();

      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (isZapietNode(added[j])) {
              schedule();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();

  /* ------------------------------------------------------------------------
     Collection nutrition sort — client-side (WCAG 4.1.3 status messages)
     ------------------------------------------------------------------------
     Shopify can't sort a collection by metafield server-side, so the custom
     Nutrition options in the sort <select> (snippets/custom-sort-options.liquid)
     are handled here: we reorder the rendered grid <li>s by the data-ff-*
     attributes that sections/custom-main-collection-product-grid.liquid writes
     onto each item.

     To sort the WHOLE collection (not just the current page), an active nutrition
     sort first loads the collection's remaining pages via the Section Rendering
     API and appends them (see loadEntireCollection), then sorts the full set.

     Integration with Dawn's faceting (assets/facets.js):
       - Dawn submits the facet form on the `input` event (debounced). When a
         NUTRITION option is chosen we stop the event in the CAPTURE phase so
         Dawn never sends the (server-invalid) sort_by, then sort in the browser.
       - Choosing a STANDARD sort clears our state and lets Dawn handle it
         natively (real server-side sort + URL).
       - When a FILTER change makes Dawn re-render #ProductGridContainer, a
         MutationObserver re-applies the active nutrition sort to the new grid.
     ------------------------------------------------------------------------ */
  (function ffCollectionSort() {
    var GRID_CONTAINER_ID = 'ProductGridContainer';
    if (!document.getElementById(GRID_CONTAINER_ID)) return; // collection/search only

    // sort value -> which data-ff-* attribute and direction
    var NUTRITION = {
      'cal-high': { attr: 'calories', dir: 'high' },
      'cal-low': { attr: 'calories', dir: 'low' },
      'protein-high': { attr: 'protein', dir: 'high' },
      'protein-low': { attr: 'protein', dir: 'low' },
      'carbs-high': { attr: 'carbs', dir: 'high' },
      'carbs-low': { attr: 'carbs', dir: 'low' },
      'sodium-high': { attr: 'sodium', dir: 'high' },
      'sodium-low': { attr: 'sodium', dir: 'low' },
      'fat-high': { attr: 'fat', dir: 'high' },
      'fat-low': { attr: 'fat', dir: 'low' },
    };

    var storageKey = 'ff-sort:' + window.location.pathname;
    var liveRegion = null;
    var observer = null;

    function getActiveSort() {
      try {
        var v = window.sessionStorage.getItem(storageKey);
        return v && NUTRITION[v] ? v : null;
      } catch (e) {
        return null;
      }
    }
    function setActiveSort(v) {
      try {
        window.sessionStorage.setItem(storageKey, v);
      } catch (e) {
        /* private mode — sort still works for this view, just not persisted */
      }
    }
    function clearActiveSort() {
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (e) {}
    }

    function sortSelects() {
      return document.querySelectorAll('select[name="sort_by"]');
    }

    // Keep every sort <select> showing the active nutrition option (without
    // firing input/change, so this never re-enters the handler).
    function syncSelects(value) {
      sortSelects().forEach(function (sel) {
        if (sel.value !== value) sel.value = value;
      });
    }

    function reorder(value) {
      var grid = document.getElementById('product-grid');
      if (!grid) return;
      var conf = NUTRITION[value];
      if (!conf) return;
      var attr = 'data-ff-' + conf.attr;
      var high = conf.dir === 'high';

      var items = Array.prototype.slice.call(grid.children).filter(function (n) {
        return n.tagName === 'LI';
      });
      items.sort(function (a, b) {
        var av = parseFloat(a.getAttribute(attr));
        var bv = parseFloat(b.getAttribute(attr));
        var aNaN = isNaN(av);
        var bNaN = isNaN(bv);
        if (aNaN && bNaN) return 0;
        if (aNaN) return 1; // missing values sink to the bottom
        if (bNaN) return -1;
        return high ? bv - av : av - bv;
      });

      var frag = document.createDocumentFragment();
      items.forEach(function (li) {
        frag.appendChild(li); // moves the existing node (preserves quick-add state)
      });
      grid.appendChild(frag);
    }

    // Disconnect the observer around our own DOM moves so reordering doesn't
    // retrigger the observer (which would loop).
    function applySort(value) {
      if (observer) observer.disconnect();
      reorder(value);
      if (observer) {
        observer.observe(document.getElementById(GRID_CONTAINER_ID), {
          childList: true,
          subtree: true,
        });
      }
    }

    /* --- Whole-collection support ------------------------------------------
       Client-side sort can only order cards that are in the DOM, and Shopify
       renders <=50 products per page. So when a nutrition sort is active we
       pull the collection's remaining pages via Dawn's Section Rendering API
       and append them — the sort then covers the WHOLE (filtered) collection,
       not just the current page. Standard (alphabetical/price) sorts stay
       server-side and paginate normally; we only load-all for nutrition. */
    var isLoadingAll = false;
    var loadedKey = null; // filter param-set we've already fully loaded

    function sectionId() {
      var grid = document.getElementById('product-grid');
      return grid ? grid.dataset.id : null;
    }

    // Filter params only (drop page + the client-side-only sort_by) so the key
    // is stable across nutrition-metric switches but changes when a filter does.
    function currentParams() {
      var p = new URLSearchParams(window.location.search);
      p.delete('page');
      p.delete('sort_by');
      return p.toString();
    }

    function hidePagination() {
      document
        .querySelectorAll('#ProductGridContainer .pagination-wrapper')
        .forEach(function (el) {
          el.style.display = 'none';
        });
    }

    function fetchPageItems(params, page, sid) {
      var url =
        window.location.pathname +
        '?section_id=' +
        encodeURIComponent(sid) +
        '&page=' +
        page +
        (params ? '&' + params : '');
      return fetch(url)
        .then(function (r) {
          if (!r.ok) return null; // HTTP error — signal failure, not end-of-pages
          return r.text();
        })
        .then(function (text) {
          if (text === null) return null;
          var grid = new DOMParser().parseFromString(text, 'text/html').getElementById('product-grid');
          if (!grid) return null; // unexpected response (no grid) — treat as failure
          return Array.prototype.slice.call(grid.children).filter(function (n) {
            return n.tagName === 'LI';
          });
        })
        .catch(function () {
          return null; // network/parse failure — distinct from a genuine empty page
        });
    }

    // Fetch pages 2..N and append until a page returns nothing (or a safety cap).
    function loadEntireCollection() {
      var sid = sectionId();
      var grid = document.getElementById('product-grid');
      if (!sid || !grid) return Promise.resolve();
      if (isLoadingAll) return Promise.resolve();

      var key = currentParams();
      if (loadedKey === key) return Promise.resolve(); // this set is already whole
      isLoadingAll = true;

      var MAX_PAGES = 20; // backstop (~1000 products)
      var page = 2;

      // Resolves true when the whole collection was loaded, false if a fetch
      // failed partway (so we leave pagination visible and don't cache the key).
      function next() {
        if (page > MAX_PAGES) return Promise.resolve(true);
        return fetchPageItems(key, page, sid).then(function (items) {
          if (items === null) return false; // fetch failed — abort, keep pagination
          if (!items.length) return true; // genuine end of pages
          if (observer) observer.disconnect();
          var frag = document.createDocumentFragment();
          items.forEach(function (li) {
            frag.appendChild(li);
          });
          grid.appendChild(frag);
          if (observer) {
            observer.observe(document.getElementById(GRID_CONTAINER_ID), {
              childList: true,
              subtree: true,
            });
          }
          page += 1;
          return next();
        });
      }

      return next().then(function (completed) {
        // Only hide pagination / cache the key when the full set actually loaded;
        // on failure leave both so a later sort/filter can retry the fetch.
        if (completed) {
          hidePagination();
          loadedKey = key;
        }
        isLoadingAll = false;
      });
    }

    // Load the whole collection (if needed) then sort it.
    function ensureFullCollectionThenSort(value) {
      loadEntireCollection().then(function () {
        applySort(value);
      });
    }

    function ensureLiveRegion() {
      if (liveRegion) return;
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    function announce(text) {
      ensureLiveRegion();
      liveRegion.textContent = '';
      // next tick so repeated identical messages are still announced
      window.setTimeout(function () {
        liveRegion.textContent = text;
      }, 50);
    }

    function optionText(select, value) {
      var opt = select.querySelector('option[value="' + value + '"]');
      return opt ? opt.textContent.trim() : value;
    }

    // Capture-phase handler: runs before Dawn's form 'input' listener.
    function onSortEvent(event) {
      var target = event.target;
      if (!target || !target.matches || !target.matches('select[name="sort_by"]')) return;
      var value = target.value;

      if (NUTRITION[value]) {
        // Intercept: keep the invalid sort_by out of Dawn's request.
        event.stopImmediatePropagation();
        event.preventDefault();
        if (getActiveSort() !== value) {
          setActiveSort(value);
          syncSelects(value);
          announce(optionText(target, value));
          ensureFullCollectionThenSort(value);
        }
      } else {
        // Standard sort chosen — hand back to Dawn (server-side sort + URL).
        if (getActiveSort()) clearActiveSort();
      }
    }

    var reapply =
      typeof debounce === 'function'
        ? debounce(reapplyNow, 100)
        : (function () {
            var t;
            return function () {
              clearTimeout(t);
              t = setTimeout(reapplyNow, 100);
            };
          })();

    function reapplyNow() {
      var active = getActiveSort();
      if (!active) return;
      // Dawn just re-rendered the grid (e.g. a filter changed): the new DOM is
      // only page 1 of the filtered set, so treat it as not-yet-loaded and pull
      // the whole filtered collection again before re-sorting.
      loadedKey = null;
      syncSelects(active);
      ensureFullCollectionThenSort(active);
    }

    function init() {
      // Both events in capture: Dawn uses 'input'; 'change' is a belt-and-braces
      // fallback. The handler is idempotent (skips when already active).
      document.addEventListener('input', onSortEvent, true);
      document.addEventListener('change', onSortEvent, true);

      observer = new MutationObserver(reapply);
      observer.observe(document.getElementById(GRID_CONTAINER_ID), {
        childList: true,
        subtree: true,
      });

      var active = getActiveSort();
      if (active) {
        syncSelects(active);
        ensureFullCollectionThenSort(active);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();

  /* ------------------------------------------------------------------------
     Footer delivery checker — drives Zapiet's own validation (no modal)
     ------------------------------------------------------------------------
     Replaces Zapiet's stock "Do We Deliver" top bar (hidden in custom.css)
     with a footer form (snippets/custom-delivery-checker.liquid). We reuse
     Zapiet's storefront JS API so the theme never hard-codes ZIP/zone rules:

       window.ZapietDeliveryValidator.checkEligibility(zip, ok, no, err)

     The result is written into a role="status" aria-live region, so screen
     readers announce "we deliver / we don't deliver" without a pop-up.

     The ZapietDeliveryValidator global is supplied by EITHER the Zapiet
     app-embed OR a Zapiet "delivery validator" app block in the header group —
     only one is needed, and as of 2026-08-05 this store uses the app block
     (the embed is disabled). Either way it loads asynchronously, so we poll
     for it before calling. CI asserts at least one loader survives; see
     .github/workflows/zapiet-embed-guard.yml.
     ------------------------------------------------------------------------ */
  (function deliveryChecker() {
    var POLL_INTERVAL = 150; // ms
    var POLL_TIMEOUT = 5000; // ms — give the Zapiet bundle time to load

    // Resolve the validator once it's available; otherwise call onUnavailable.
    function withValidator(onReady, onUnavailable) {
      var waited = 0;
      (function poll() {
        var v = window.ZapietDeliveryValidator;
        if (v && typeof v.checkEligibility === 'function') {
          onReady(v);
          return;
        }
        if (waited >= POLL_TIMEOUT) {
          onUnavailable();
          return;
        }
        waited += POLL_INTERVAL;
        setTimeout(poll, POLL_INTERVAL);
      })();
    }

    function setResult(els, template, zip, stateClass) {
      var text = (template || '').replace(/\{zip\}/g, zip);
      els.result.classList.remove('is-loading', 'is-eligible', 'is-not-eligible', 'is-error');
      if (stateClass) els.result.classList.add(stateClass);
      // Re-assigning textContent on the live region triggers the announcement.
      els.result.textContent = text;
    }

    function setBusy(els, busy) {
      if (!els.submit) return;
      els.submit.disabled = busy;
      els.submit.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    function wire(form) {
      var msg = {
        eligible: form.getAttribute('data-msg-eligible') || '',
        notEligible: form.getAttribute('data-msg-not-eligible') || '',
        error: form.getAttribute('data-msg-error') || '',
        empty: form.getAttribute('data-msg-empty') || '',
        loading: form.getAttribute('data-msg-loading') || '',
      };
      var els = {
        input: form.querySelector('[data-ff-delivery-zip]'),
        submit: form.querySelector('[data-ff-delivery-submit]'),
        result: form.parentNode.querySelector('[data-ff-delivery-result]'),
      };
      if (!els.input || !els.result) return;

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var zip = (els.input.value || '').trim();
        if (!zip) {
          setResult(els, msg.empty, zip, 'is-error');
          els.input.focus();
          return;
        }

        setBusy(els, true);
        if (msg.loading) setResult(els, msg.loading, zip, 'is-loading');

        withValidator(
          function (validator) {
            validator.checkEligibility(
              zip,
              function eligible(response) {
                setBusy(els, false);
                setResult(els, msg.eligible, zip, 'is-eligible');
                // Carry the validated postal code into cart/checkout, exactly
                // as the stock widget does. Guarded — older app versions may
                // not expose this; the footer message works regardless.
                try {
                  if (typeof validator.eligibleForDelivery === 'function') {
                    validator.eligibleForDelivery(response);
                  }
                } catch (err) {
                  /* non-fatal: message already shown */
                }
              },
              function notEligible() {
                setBusy(els, false);
                setResult(els, msg.notEligible, zip, 'is-not-eligible');
              },
              function error() {
                setBusy(els, false);
                setResult(els, msg.error, zip, 'is-error');
              }
            );
          },
          function unavailable() {
            setBusy(els, false);
            setResult(els, msg.error, zip, 'is-error');
          }
        );
      });
    }

    function init() {
      document.querySelectorAll('[data-ff-delivery-checker]').forEach(wire);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();

  /* ------------------------------------------------------------------------
     Yotpo star rating — "Jump to reviews" scroll (WCAG 2.4.3 / 2.4.1)
     ------------------------------------------------------------------------
     On product pages the Yotpo Star Rating block renders a real, labelled
     <button class="yotpo-sr-bottom-line-button"> ("…Jump to reviews."), but in
     this Online Store 2.0 app-block layout the Star Rating and the Reviews
     Widget live in SEPARATE sections, so Yotpo's native jump handler never
     locates the reviews widget and clicking the button does nothing (verified
     live: scrollY stayed at 0).

     We can't edit the app's output, so we add the scroll glue here. Delegated
     on document because both widgets hydrate asynchronously. We don't
     preventDefault — the button isn't a link and has no working default, so we
     simply add the missing scroll + move focus into the reviews region for
     keyboard / screen-reader users.

     Target resolution prefers the Reviews app-block wrapper (a stable,
     theme-controlled id) over Yotpo's own classes, and ignores the empty
     placeholder <div class="yotpo-widget-instance"> Yotpo appends to <body>.
     ------------------------------------------------------------------------ */
  (function yotpoJumpToReviews() {
    var BUTTON_SELECTOR = '.yotpo-sr-bottom-line-button, .yotpo-sr-bottom-line-summary';

    // The Reviews Widget container to scroll to. Prefer the app-block wrapper
    // (id contains the block name from templates/product.json); fall back to
    // Yotpo's rendered reviews list. We DON'T gate on height: the wrapper is
    // server-rendered and present from page load but its Yotpo widget hydrates
    // asynchronously (often 0-height at first click). Returning it unhydrated is
    // the point — the chase loop re-targets as it grows. Both selectors are
    // specific enough to never match the empty body-level placeholder
    // (`.yotpo-widget-instance`, which has no such id and no `.yotpo-reviews`).
    function findReviewsTarget() {
      var wrapper = document.querySelector(
        '[id^="shopify-block"][id*="yotpo_product_reviews_reviews"]'
      );
      if (wrapper) return wrapper;

      var list = document.querySelector('.yotpo-reviews');
      if (list) return list.closest('.yotpo-widget-instance') || list;

      return null;
    }

    // Sticky header overlaps the top of the viewport once pinned; Dawn keeps its
    // height in --header-height. Offset by it (plus a small gap) so the reviews
    // heading isn't tucked underneath.
    function headerOffset() {
      var raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      var h = parseInt(raw, 10);
      return (isNaN(h) ? 0 : h) + 12;
    }

    function desiredTop(target) {
      return Math.max(0, target.getBoundingClientRect().top + window.scrollY - headerOffset());
    }

    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // Only one chase runs at a time: a new click cancels the previous one (and
    // tears down its listeners) before starting fresh.
    var stopActiveChase = null;
    var CANCEL_EVENTS = ['wheel', 'touchstart', 'keydown'];

    function jumpTo(target) {
      // Move focus into the reviews region for keyboard / SR users. tabindex=-1
      // makes the container programmatically focusable; we leave it set
      // (harmless) so repeat jumps stay cheap. preventScroll so focus doesn't
      // fight the smooth scroll below.
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });

      if (stopActiveChase) stopActiveChase(); // supersede any in-flight chase

      // The reviews widget sits below related-products, whose images lazy-load
      // and grow the page WHILE we scroll — a one-shot scrollTo undershoots. So
      // we re-issue a scroll toward the (moving) target until it settles, capped
      // in time. Any manual scroll/keypress cancels the chase so we never fight
      // the user. Reduced-motion users get instant jumps (no scroll animation).
      var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      var ticks = 0;
      var timer = null;

      function cleanup() {
        if (timer) clearTimeout(timer);
        CANCEL_EVENTS.forEach(function (t) {
          window.removeEventListener(t, cleanup);
        });
        if (stopActiveChase === cleanup) stopActiveChase = null;
      }
      stopActiveChase = cleanup;

      CANCEL_EVENTS.forEach(function (t) {
        window.addEventListener(t, cleanup, { passive: true });
      });

      (function chase() {
        if (ticks++ > 10) { cleanup(); return; } // ~1.5s cap
        var want = desiredTop(target);
        if (Math.abs(window.scrollY - want) > 2) {
          window.scrollTo({ top: want, behavior: behavior });
        }
        timer = setTimeout(chase, 150);
      })();
    }

    document.addEventListener('click', function (event) {
      var btn = event.target.closest && event.target.closest(BUTTON_SELECTOR);
      if (!btn) return;
      var target = findReviewsTarget();
      if (!target) return; // no reviews widget on this page — leave Yotpo's button alone
      jumpTo(target);
    });
  })();

  /* ------------------------------------------------------------------------
     Third-party widget a11y patches — Yotpo + Loop
     ------------------------------------------------------------------------
     App-rendered markup issues we can't fix at source (and that re-render,
     so we re-apply on mutation, same approach as the Zapiet patch above):

     1) Yotpo — empty review-title heading. A review left without a title still
        renders `<div class="content-title" role="heading" aria-level="3">` with
        only whitespace, which scanners flag as an empty (h3) heading. It's not a
        real heading when empty, so we strip the heading semantics (role +
        aria-level) — leaving a harmless empty div. (Whitespace content means a
        CSS `:empty` rule can't catch it, hence JS.)

     2) Loop Subscriptions — hidden data-carrier inputs (`input.loop-hidden`,
        e.g. `loop_variant_id`) ship with no label. They're `display:none` and
        not user-facing, but label-presence scanners flag them regardless, so we
        give each a name-derived `aria-label` (only when it has none).

     3) Yotpo Rewards (Swell) — the rewards-page buttons (e.g. "Join now",
        "Log in") render with `aria-label=""`. An empty aria-label can blank out
        the accessible name in AT that honors it, leaving an unlabelled button.
        Yotpo's admin doesn't expose these, so where the button has visible text
        to fall back to we strip the empty label and let the text name it.

     4) Loop Subscriptions — the tooltip icon (`.loop-tooltip-image svg`) has
        no name or aria-hidden, so WAVE flags it as missing alt text. It is
        decorative: the tooltip's label and description text sit beside it.
        A false positive today — the tooltip is `display:none` and Loop only
        shows it when its "subscription popup" setting is on (it is off) —
        but hiding it keeps scans clean if that changes. The markup is
        in Loop's own snippet (loop-widget-standard.liquid), which the
        installer regenerates, so we hide the icon here instead.

     NOTE: the duplicate Loop *widget* (the "multiple form label" / duplicate-id
     issue) is fixed at source in sections/main-product.liquid, not here. */
  (function thirdPartyReviewSubscriptionA11y() {
    function patch() {
      // 1) Yotpo empty review-title headings → drop heading semantics.
      document
        .querySelectorAll('.yotpo-main-widget .content-title[role="heading"]')
        .forEach(function (h) {
          if ((h.textContent || '').trim() === '') {
            h.removeAttribute('role');
            h.removeAttribute('aria-level');
          }
        });

      // 2) Loop hidden inputs without an accessible name → label them.
      document
        .querySelectorAll('input.loop-hidden, select.loop-hidden, textarea.loop-hidden')
        .forEach(function (el) {
          var hasName =
            (el.getAttribute('aria-label') || '').trim() ||
            el.getAttribute('aria-labelledby') ||
            (el.id && document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(el.id) : el.id) + '"]'));
          if (!hasName) {
            el.setAttribute('aria-label', el.name || el.className || 'Hidden field');
          }
        });

      // 3) Yotpo Rewards (Swell) buttons with an empty aria-label → strip it so
      //    the visible text becomes the accessible name. Guarded on visible text
      //    so an icon-only control is never blank-stripped into namelessness.
      document
        .querySelectorAll('button.yotpo-button-style[aria-label], a.yotpo-button-style[aria-label]')
        .forEach(function (el) {
          if (
            (el.getAttribute('aria-label') || '').trim() === '' &&
            (el.textContent || '').trim() !== ''
          ) {
            el.removeAttribute('aria-label');
          }
        });

      // 4) Loop tooltip icon → decorative, hide it from assistive technology.
      document.querySelectorAll('.loop-tooltip-image svg').forEach(function (svg) {
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
      });
    }

    function isRelevant(node) {
      if (node.nodeType !== 1) return false;
      var cls = typeof node.className === 'string' ? node.className : '';
      if (/yotpo|loop-hidden|loop-tooltip/.test(cls)) return true;
      return !!(
        node.querySelector &&
        node.querySelector('.yotpo-main-widget, .loop-hidden, .loop-tooltip-image, button.yotpo-button-style, a.yotpo-button-style')
      );
    }

    function init() {
      patch();
      var schedule =
        typeof debounce === 'function'
          ? debounce(patch, 200)
          : (function () {
              var t;
              return function () {
                clearTimeout(t);
                t = setTimeout(patch, 200);
              };
            })();

      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (isRelevant(added[j])) {
              schedule();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();

  /* ------------------------------------------------------------------------
     Delivery checker — "attention" highlight when jumped to (#delivery-checker)
     ------------------------------------------------------------------------
     The footer delivery checker (snippets/custom-delivery-checker.liquid) is the
     target of in-page "check your zip code" links. Landing there via a hash jump
     isn't always obvious — the page just scrolls — so we briefly ring the checker
     to show what was targeted, and move focus into it so keyboard / screen-reader
     users get the same signal (an in-page link otherwise scrolls without moving
     focus).

     Accessibility:
       - No strobe. Motion users get a slow 2-pulse ring (~1s between flashes,
         ~2s total ≈ 1 flash/second, well under WCAG 2.3.1's 3-flashes/second).
         The ring is a box-shadow, so it never changes the contrast of the
         text/inputs inside.
       - prefers-reduced-motion: NO animation — a single static ring shows and is
         cleared after ~2s (the pulse vs. static ring is in custom.css).
       - Focus moves to the ZIP input (a real, visibly-focusable control with its
         own :focus outline) so keyboard users keep a visible focus indicator
         after the pulse ends (WCAG 2.4.7) and the next Tab lands on the submit
         button — not into a tabindex=-1 dead container. Falls back to the wrapper
         only if the input is missing. preventScroll so we don't fight the hash
         scroll.

     Fires on same-page (and repeat) clicks of any link to the anchor, and when a
     page loads already at #delivery-checker (cross-page link, refresh, bookmark). */
  (function deliveryCheckerAttention() {
    var ID = 'delivery-checker';
    var prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Focus the ZIP input — a real, visibly-focusable control (it has its own
    // :focus outline in custom.css), so focus stays visible after the pulse ends
    // and the next Tab lands on the submit button. Fall back to the wrapper (made
    // programmatically focusable) only if the input isn't found.
    function focusTargetFor(el) {
      var input = el.querySelector('[data-ff-delivery-zip], .ff-delivery-checker__input');
      if (input) return input;
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      return el;
    }

    function flash() {
      var el = document.getElementById(ID);
      if (!el) return;
      // Move focus to the input; preventScroll so we don't fight the hash scroll.
      var target = focusTargetFor(el);
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        target.focus();
      }
      // Restart the pulse even if already applied (repeat clicks): remove, force
      // reflow, re-add.
      el.classList.remove('ff-attention');
      void el.offsetWidth;
      el.classList.add('ff-attention');
      // Reduced motion has no animationend to clean up after — clear on a timer.
      if (prefersReduce.matches) {
        setTimeout(function () {
          el.classList.remove('ff-attention');
        }, 2000);
      }
    }

    // Motion users: drop the class when the pulse finishes.
    document.addEventListener('animationend', function (e) {
      if (e.animationName === 'ff-attention-pulse' && e.target.id === ID) {
        e.target.classList.remove('ff-attention');
      }
    });

    // Same-page (and repeat) clicks on any link pointing at the checker. Runs
    // after the browser has processed the jump.
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href$="#' + ID + '"]');
      if (link) setTimeout(flash, 0);
    });

    // Arriving with the hash already set (cross-page link, refresh, bookmark).
    function onLoadHash() {
      if (location.hash === '#' + ID) flash();
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', onLoadHash);
    } else {
      onLoadHash();
    }
  })();

  /* ------------------------------------------------------------------------
     Cart Checkout button — neutralize Zapiet's cart-page validation gate
     ------------------------------------------------------------------------
     Zapiet's storefront bundle runs `Zapiet.setupValidationChecks()`, which
     preventDefault()s the cart Checkout button until its pickup/delivery picker
     is satisfied. This theme deliberately HIDES that picker (#storePickupApp in
     custom.css, client request) and defers method selection to Zapiet's
     CHECKOUT widget, so the cart-page gate can never be satisfied and the
     Checkout button goes dead.

     Confirmed at runtime (2026-07-10, theme dev + Playwright): with the embed
     re-enabled for the footer ZIP checker, a synthetic click on the cart
     Checkout button was preventDefault'd EVEN with a method pre-selected
     (getSelectedCheckoutMethod() === 'pickup'); calling
     Zapiet.removeValidationChecks() immediately unblocked it.

     So we remove the cart-page gate ourselves. This is the theme-side,
     update-safe equivalent of turning OFF Zapiet's "Cart widget" in the app
     dashboard — a per-theme setting that keeps getting lost whenever the app /
     embed is toggled. The delivery method + location are still committed to the
     cart by Zapiet's "Pre-select delivery option on cart page" setting and
     captured at checkout, so orders keep their fulfillment data.
     ⚠️ VERIFY with a REAL test order that method/location/date still attach.

     Zapiet arms its checks asynchronously and can re-arm them when the cart
     re-renders, so we (a) poll until the API exists, (b) clear a few times over
     a short window to win the setup race, and (c) re-clear on every cartUpdate.
     If nothing loads Zapiet at all (e.g. pre-launch), this no-ops harmlessly.

     NOTE (2026-08-05): the client's own Zapiet reconfiguration now also turns
     the cart widget off app-side — #storePickupApp no longer renders on the
     cart at all, and the Checkout button was verified live as enabled and
     clickable. This IIFE is therefore belt-and-braces rather than the active
     fix today. Keep it: the app-side toggle is exactly the per-theme setting
     that has repeatedly been lost, and this costs nothing when there is
     nothing to clear.
     ------------------------------------------------------------------------ */
  (function zapietCheckoutButtonFix() {
    var POLL_INTERVAL = 150; // ms
    var POLL_TIMEOUT = 8000; // ms — the Zapiet bundle can load late
    var RECLEAR_DELAYS = [0, 500, 1500, 3000]; // beat late setupValidationChecks()

    function clearChecks() {
      try {
        if (window.Zapiet && typeof window.Zapiet.removeValidationChecks === 'function') {
          window.Zapiet.removeValidationChecks();
        }
      } catch (e) {
        /* non-fatal — never let this break the page */
      }
    }

    // Run cb once Zapiet's API is present; give up quietly after POLL_TIMEOUT
    // (embed disabled → nothing to neutralize).
    function whenZapietReady(cb) {
      var waited = 0;
      (function poll() {
        if (window.Zapiet && typeof window.Zapiet.removeValidationChecks === 'function') {
          cb();
          return;
        }
        if (waited >= POLL_TIMEOUT) return;
        waited += POLL_INTERVAL;
        setTimeout(poll, POLL_INTERVAL);
      })();
    }

    function init() {
      whenZapietReady(function () {
        RECLEAR_DELAYS.forEach(function (d) {
          setTimeout(clearChecks, d);
        });
        // Dawn re-renders the cart (and its Checkout button) on cart changes;
        // Zapiet may re-arm its gate against the new DOM, so clear again after.
        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
            setTimeout(clearChecks, 0);
            setTimeout(clearChecks, 300);
          });
        }
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();

  /* ------------------------------------------------------------------------
     "Leaving soon" — describe the Add to cart button with the availability note
     ------------------------------------------------------------------------
     The ff_leaving_soon product block renders "Available until <date>" after the
     Add to cart button. Sighted users read it on the way past; a keyboard or
     screen-reader user tabbing straight to the button never encounters it, and
     so can buy a seasonal item without ever learning it's about to leave.

     Pointing the button's aria-describedby at the note fixes that: the date is
     announced as part of focusing the button, without duplicating it as visible
     text and without a live region (the date is static — see the comment on the
     block in sections/main-product.liquid for why role="status" is wrong here).

     Done in JS rather than in Liquid because the note is an optional, movable
     theme-editor block: buy-buttons.liquid can't know whether it exists or what
     its id is, and forking that core snippet to find out would cost more than
     this. Re-applied on variantChange because Dawn swaps the buy-buttons markup
     wholesale, which drops the attribute.
     ------------------------------------------------------------------------ */
  (function ffLeavingSoonDescribes() {
    var NOTE_PREFIX = 'FFLeavingSoon-';
    var BUTTON_PREFIX = 'ProductSubmitButton-';

    function describe(note) {
      if (!note || !note.id || note.id.indexOf(NOTE_PREFIX) !== 0) return;

      var button = document.getElementById(BUTTON_PREFIX + note.id.slice(NOTE_PREFIX.length));
      if (!button) return;

      // Append rather than overwrite — apps (Loop) may already describe the
      // button, and clobbering their reference would lose their message.
      var existing = (button.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (existing.indexOf(note.id) !== -1) return;

      existing.push(note.id);
      button.setAttribute('aria-describedby', existing.join(' '));
    }

    function apply() {
      var notes = document.querySelectorAll('.ff-product-leaving-soon .ff-leaving-soon-note[id]');
      Array.prototype.slice.call(notes).forEach(describe);
    }

    function init() {
      apply();

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        // The new button is in the DOM by the time the event fires, but Dawn
        // finishes its swap via HTMLUpdateUtility's double buffer — defer a tick.
        subscribe(PUB_SUB_EVENTS.variantChange, function () {
          setTimeout(apply, 0);
        });
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();

  /* ------------------------------------------------------------------------
     "Leaving soon" — cart removal dialog for items past their availability date
     ------------------------------------------------------------------------
     Rebuild of the old Flow theme's SweetAlert modal. That version auto-fired
     with allowEscapeKey and allowOutsideClick both false — no way out by
     keyboard (WCAG 2.1.2) — offered a single "OK" that removed items, and drove
     the removal off a `.target-removal .js-qty__num` selector that matched
     nothing in the cart markup, so it very likely never worked.

     What changed:
       - Native <dialog> + showModal(). The platform supplies the focus trap,
         Escape-to-close, background inertness and focus restore, all of which
         are easy to get subtly wrong by hand.
       - Two actions, with autofocus on "Keep in cart". A destructive button
         should never be the thing focus lands on when a dialog opens.
       - Dismissing loses nothing: the per-row "Leaving Soon!" notes stay on the
         page, so the information survives the dialog either way.
       - Removal is keyed on the line-item key (item.key), not the numeric line
         index. Indices renumber as lines are removed, so index-based removal
         deletes the wrong rows once there's more than one.

     After a successful removal we reload rather than swapping the cart sections
     the way cart.js does. The cart carries Zapiet's markup, and re-rendering it
     underneath Zapiet is the known cause of its checkout button going dead; a
     reload after an explicit confirmation is cheap and leaves Zapiet to
     re-initialise cleanly.

     Note this dialog does open on page load, which is in tension with WCAG
     3.2.5. It is a deliberate, flagged tradeoff: the items genuinely cannot be
     ordered, so the alternative is a customer discovering it at checkout.
     ------------------------------------------------------------------------ */
  (function ffLeavingSoonCart() {
    var STORAGE_KEY = 'ffLeavingSoonRemoved';

    function announce(message) {
      if (!message) return;
      var region = document.getElementById('cart-live-region-text');
      if (!region) return;
      region.removeAttribute('aria-hidden');
      // Let the region be observed as live before its text changes, or the
      // insertion can land in the same paint and go unannounced.
      setTimeout(function () {
        region.textContent = message;
      }, 500);
    }

    function announceCompletedRemoval() {
      var message;
      try {
        message = sessionStorage.getItem(STORAGE_KEY);
        if (message) sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        return; // private mode / storage disabled — nothing to announce
      }
      announce(message);
    }

    function removeLines(keys) {
      // Sequential, not parallel: Shopify serialises cart writes, and
      // concurrent /cart/change.js calls can return stale carts.
      return keys.reduce(function (chain, key) {
        return chain.then(function () {
          var config = Object.assign({}, fetchConfig(), {
            body: JSON.stringify({ id: key, quantity: 0 }),
          });
          return fetch(routes.cart_change_url, config).then(function (response) {
            if (!response.ok) throw new Error('cart change failed');
            return response.json();
          });
        });
      }, Promise.resolve());
    }

    function init() {
      announceCompletedRemoval();

      var dialog = document.getElementById('FFLeavingSoonDialog');
      if (!dialog || typeof dialog.showModal !== 'function') return;

      var keepButton = dialog.querySelector('[data-ff-ls-keep]');
      var removeButton = dialog.querySelector('[data-ff-ls-remove]');
      var errorNode = dialog.querySelector('[data-ff-ls-error]');
      if (!removeButton) return;

      // showModal() makes the background inert but does not stop it scrolling.
      dialog.addEventListener('close', function () {
        document.body.classList.remove('overflow-hidden');
      });

      if (keepButton) {
        keepButton.addEventListener('click', function () {
          dialog.close();
        });
      }

      removeButton.addEventListener('click', function () {
        var rows = document.querySelectorAll('.cart-item[data-ff-line-key]');
        var keys = Array.prototype.slice
          .call(rows)
          .map(function (row) {
            return row.getAttribute('data-ff-line-key');
          })
          .filter(Boolean);

        if (!keys.length) {
          dialog.close();
          return;
        }

        removeButton.setAttribute('disabled', 'disabled');
        if (errorNode) errorNode.textContent = '';

        removeLines(keys)
          .then(function () {
            try {
              sessionStorage.setItem(STORAGE_KEY, removeButton.dataset.ffLsRemovedMessage || '');
            } catch (e) {
              /* storage unavailable — the reload still happens, just silently */
            }
            window.location.reload();
          })
          .catch(function () {
            removeButton.removeAttribute('disabled');
            // Keep the dialog open and say so in its own role="alert" region,
            // rather than reloading into a cart that still has the bad lines.
            if (errorNode) errorNode.textContent = removeButton.dataset.ffLsErrorMessage || '';
          });
      });

      document.body.classList.add('overflow-hidden');
      dialog.showModal();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();

  /* ------------------------------------------------------------------------
     POS Cafe Online — modal focus + lost-focus workaround (WCAG 2.4.3)
     ------------------------------------------------------------------------
     TEMPORARY vendor workaround — reported to POS Cafe (support@poscafe.app).
     Remove once their useFocusTrap is fixed; re-test with the steps below.

     The POS Cafe Online app block (templates/page.order.json) opens its
     product / cart modals as role="dialog" aria-modal="true" in a portal
     (<body> > div.poscafe-app > .modal). Its own focus trap
     (pos-cafe-online-useFocusTrap.js) captures the container and close button
     when the modal opens, but the modal re-renders straight after, so the
     trap holds DETACHED nodes: focus() on the close button is a no-op (focus
     stays on the "+" behind the modal) and its Tab handler compares against
     stale first/last elements, so Tab walks the whole menu behind the dialog.
     Verified 2026-10-27 by logging focus() calls (target isConnected: false).

     Fix here: when a POS Cafe dialog is open, move focus into it (close
     button) and wrap Tab / Shift+Tab, looking the focusable elements up AT
     KEYPRESS TIME (Dawn's getFocusableElements) so re-renders can't strand
     us. Deliberately NOT Dawn's trapFocus(): it snapshots first/last at call
     time (the same defect), and re-calling it per re-render would also
     select-all a focused text input. Escape is left to the app (it works).
     Focus return on close is NOT: the app's restore targets the same stale
     nodes, so focus falls to <body>. We remember the opener (and its
     aria-label, since the menu re-renders it as a new node) and focus it
     again on close.

     Also: "Change Location" swaps the menu view out and removes the focused
     button, dropping focus to <body>. When focus is lost from inside the app
     that way, move it to the app's first heading (tabindex=-1) so keyboard /
     screen-reader users land at the new view.

     Test: on the order page, Tab to a "+" button, press Enter → focus is on
     "Close modal"; Tab/Shift+Tab stay inside the dialog; Escape returns focus
     to the "+". Then activate "Change Location" → focus lands on the location
     heading, not the top of the page. */
  (function posCafeFocusFix() {
    var APP = '.poscafe-app, [data-poscafe-online]';
    var DIALOG = '.poscafe-app [role="dialog"][aria-modal="true"]';
    var openDialog = null;
    var opener = null;
    var openerLabel = '';
    var restorePending = false;
    var lastAppRoot = null;
    var lastAppFocus = null;

    function isShown(el) {
      if (!el.isConnected || !el.getClientRects().length) return false;
      // Bootstrap-style fade: only treat it as open once it has .show.
      return !el.classList.contains('fade') || el.classList.contains('show');
    }

    function focusables(container) {
      return getFocusableElements(container).filter(function (el) {
        return el.getClientRects().length && !el.closest('[aria-hidden="true"], [inert]');
      });
    }

    function currentDialog() {
      var dialogs = document.querySelectorAll(DIALOG);
      for (var i = dialogs.length - 1; i >= 0; i--) {
        if (isShown(dialogs[i])) return dialogs[i];
      }
      return null;
    }

    // The app re-renders the menu while the dialog is open, so the "+" that
    // opened it may be a new node by the time it closes. Fall back to the
    // button with the same accessible name.
    function findOpener() {
      if (opener && opener.isConnected) return opener;
      if (!openerLabel) return null;
      var selector = '[aria-label="' + (window.CSS && CSS.escape ? CSS.escape(openerLabel) : openerLabel) + '"]';
      var roots = document.querySelectorAll('[data-poscafe-online]');
      for (var i = 0; i < roots.length; i++) {
        var match = roots[i].querySelector(selector);
        if (match) return match;
      }
      return null;
    }

    // Focus is "lost" when it sits on <body> or on a node the app has removed
    // or hidden (the browser doesn't always move it off a hidden element).
    function isLost(el) {
      return !el || el === document.body || !el.isConnected || !el.getClientRects().length;
    }

    function sync() {
      var dialog = currentDialog();
      if (dialog) {
        var wasOpen = !!openDialog;
        if (!wasOpen) {
          restorePending = false;
          var active = document.activeElement;
          opener = active && active !== document.body && !dialog.contains(active) ? active : null;
          openerLabel = opener ? opener.getAttribute('aria-label') || '' : '';
        }
        openDialog = dialog;
        // Only move focus if it isn't already inside the dialog, so a
        // re-render while the user is working in it never steals focus.
        if (!dialog.contains(document.activeElement)) {
          var target = dialog.querySelector('.btn-close') || focusables(dialog)[0];
          if (!target) {
            if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
            target = dialog;
          }
          // First open only: a later "focus outside" means the user clicked
          // out or the app moved it — don't fight that on every mutation.
          if (!wasOpen || !document.activeElement || document.activeElement === document.body) {
            target.focus();
          }
        }
        return;
      }

      if (openDialog) {
        // Dialog closing. The app drops .show first and removes the node
        // ~300ms later, so focus may still be on the close button here; keep
        // the restore pending until focus is actually lost.
        openDialog = null;
        restorePending = true;
      }

      if (restorePending) {
        if (!isLost(document.activeElement)) {
          // Focus went somewhere real (the app restored it, or the user
          // moved on) — but not while it's still on the dialog being removed.
          if (!document.activeElement.closest('.poscafe-app [role="dialog"]')) {
            restorePending = false;
            opener = null;
            openerLabel = '';
          }
          return;
        }
        // The app's own restore targets stale nodes, so focus fell to <body>;
        // send it back to the opener.
        var back = findOpener();
        restorePending = false;
        opener = null;
        openerLabel = '';
        if (back) {
          back.focus();
          return;
        }
      }

      // Focus rescue: focus fell to <body> because the focused app element
      // was removed or hidden (e.g. "Change Location"). Land on the new
      // view's heading.
      if (isLost(document.activeElement) && lastAppFocus && isLost(lastAppFocus) && lastAppRoot && lastAppRoot.isConnected) {
        // The new view may still be loading; if there's no heading yet, keep
        // lastAppFocus and try again on the next mutation.
        var heading = lastAppRoot.querySelector('h1, h2, h3');
        if (heading) {
          if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
          heading.focus();
          lastAppFocus = null;
        }
      }
    }

    function onKeydown(event) {
      if (event.key !== 'Tab' || !openDialog || !openDialog.isConnected) return;
      var items = focusables(openDialog);
      if (!items.length) {
        event.preventDefault();
        openDialog.focus();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      var inside = openDialog.contains(active);
      if (event.shiftKey && (!inside || active === first || active === openDialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (!inside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    }

    function init() {
      // Only on pages that render the POS Cafe Online block.
      if (!document.querySelector('[data-poscafe-online]')) return;

      document.addEventListener('keydown', onKeydown);
      document.addEventListener('focusin', function (event) {
        var root = event.target.closest && event.target.closest(APP);
        if (root) {
          lastAppRoot = root.matches('[data-poscafe-online]') ? root : root.closest('[data-poscafe-online]') || root;
          lastAppFocus = event.target;
        } else {
          lastAppFocus = null;
        }
      });
      // A click outside the app (even on blank space, which focuses <body>)
      // is the user leaving it — never "rescue" focus back after that.
      document.addEventListener('pointerdown', function (event) {
        if (!(event.target.closest && event.target.closest(APP))) lastAppFocus = null;
      });

      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          sync();
        });
      }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();
})();
